import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Container } from '../../../components/commons';
import useToast from '../../../hooks/useToast';
import { ttdService, TtdDocument, TtdSummary, CpadTerm } from '../../../services/ttd.service';
import {
  FaHourglassHalf,
  FaBoxArchive,
  FaTriangleExclamation,
  FaLandmark,
  FaTrashCan,
  FaRotate,
  FaFileCircleCheck,
  FaFileSignature,
  FaFilter,
  FaPrint,
  FaScaleBalanced,
} from 'react-icons/fa6';
import { format } from 'date-fns';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div<{ active?: boolean; color: string }>`
  background: #ffffff;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${({ color }) => color};
  cursor: pointer;
  transition: all 0.2s;
  ${({ active }) => active && 'background-color: #f8fafc; border: 2px solid #0284c7;'}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .label {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .value {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    margin-top: 8px;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' | 'warning' }>`
  background-color: ${({ variant }) =>
    variant === 'secondary'
      ? '#f1f5f9'
      : variant === 'danger'
      ? '#ef4444'
      : variant === 'warning'
      ? '#d97706'
      : '#0284c7'};
  color: ${({ variant }) => (variant === 'secondary' ? '#334155' : '#ffffff')};
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Badge = styled.span<{ variant: string }>`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background-color: ${({ variant }) => {
    switch (variant) {
      case 'corrente':
        return '#e0f2fe';
      case 'intermediario':
        return '#fef3c7';
      case 'apto_eliminacao':
        return '#fee2e2';
      case 'recolhido_permanente':
        return '#dcfce7';
      case 'eliminado':
        return '#f1f5f9';
      default:
        return '#f1f5f9';
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case 'corrente':
        return '#0369a1';
      case 'intermediario':
        return '#b45309';
      case 'apto_eliminacao':
        return '#b91c1c';
      case 'recolhido_permanente':
        return '#15803d';
      case 'eliminado':
        return '#64748b';
      default:
        return '#64748b';
    }
  }};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;

  th {
    padding: 12px 14px;
    background-color: #f8fafc;
    color: #475569;
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 700;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    font-size: 13px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
`;

const ModalBox = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
  }

  input, select, textarea {
    padding: 9px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 13px;
    outline: none;

    &:focus {
      border-color: #0284c7;
    }
  }
`;

const CpadPage: React.FC = () => {
  const toast = useToast();
  const [summary, setSummary] = useState<TtdSummary | null>(null);
  const [activeStatus, setActiveStatus] = useState<string>('todos');
  const [documents, setDocuments] = useState<TtdDocument[]>([]);
  const [terms, setTerms] = useState<CpadTerm[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'documents' | 'terms'>('documents');
  const [search, setSearch] = useState('');

  // Modal Termo CPAD
  const [isTermModalOpen, setIsTermModalOpen] = useState(false);
  const [termType, setTermType] = useState<'eliminacao' | 'recolhimento_permanente'>('eliminacao');
  const [termNumber, setTermNumber] = useState('');
  const [justification, setJustification] = useState('');
  const [legalBasis, setLegalBasis] = useState('Lei Federal nº 8.159/1991 e Tabela de Temporalidade Documental (TTD)');
  const [committeeMembers, setCommitteeMembers] = useState('Presidente da CPAD, Arquivista Responsável, Representante Jurídico');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sumRes, docsRes, termsRes] = await Promise.all([
        ttdService.getStatusSummary(),
        ttdService.listDocuments({ status: activeStatus, search }),
        ttdService.listCpadTerms(),
      ]);
      setSummary(sumRes.data);
      setDocuments(docsRes.data || []);
      setTerms(termsRes.data || []);
    } catch (err) {
      toast.errorToast('Erro ao carregar dados da TTD / CPAD.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setSelectedDocIds([]);
  }, [activeStatus]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData();
  };

  const handleRecalculateAll = async () => {
    try {
      setLoading(true);
      toast.infoToast('Recalculando temporalidade com base na TTD...');
      const res = await ttdService.recalculateAll();
      toast.successToast(res.data?.message || 'Temporalidade recalculada com sucesso!');
      fetchData();
    } catch (err) {
      toast.errorToast('Erro ao recalcular temporalidade.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedDocIds(documents.map((d) => d._id));
    } else {
      setSelectedDocIds([]);
    }
  };

  const handleSelectDoc = (id: string) => {
    if (selectedDocIds.includes(id)) {
      setSelectedDocIds(selectedDocIds.filter((item) => item !== id));
    } else {
      setSelectedDocIds([...selectedDocIds, id]);
    }
  };

  const handleOpenTermModal = (type: 'eliminacao' | 'recolhimento_permanente') => {
    if (selectedDocIds.length === 0) {
      toast.errorToast('Selecione ao menos um documento na tabela.');
      return;
    }
    const currentYear = new Date().getFullYear();
    const count = terms.length + 1;
    setTermType(type);
    setTermNumber(
      `${String(count).padStart(3, '0')}/${currentYear}-${type === 'eliminacao' ? 'ELIMINACAO' : 'PERMANENTE'}`
    );
    setJustification(
      type === 'eliminacao'
        ? 'Cumprimento integral dos prazos de guarda em fase corrente e intermediária com base na Tabela de Temporalidade Documental (TTD).'
        : 'Destinação final como Guarda Permanente para preservação do patrimônio histórico e comprobatório.'
    );
    setIsTermModalOpen(true);
  };

  const handleSaveTerm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const membersArray = committeeMembers
        .split(',')
        .map((m) => m.trim())
        .filter((m) => m.length > 0);

      await ttdService.createCpadTerm({
        termNumber,
        type: termType,
        committeeMembers: membersArray,
        justification,
        legalBasis,
        documentIds: selectedDocIds,
      });

      toast.successToast(`Termo ${termNumber} lavrado e aplicado com sucesso!`);
      setIsTermModalOpen(false);
      setSelectedDocIds([]);
      fetchData();
    } catch (err: any) {
      toast.errorToast(err?.response?.data?.message || 'Erro ao registrar Termo da CPAD.');
    }
  };

  const renderStatusLabel = (status: string) => {
    switch (status) {
      case 'corrente':
        return 'Fase Corrente';
      case 'intermediario':
        return 'Fase Intermediária';
      case 'apto_eliminacao':
        return 'Apto p/ Eliminação';
      case 'recolhido_permanente':
        return 'Guarda Permanente';
      case 'eliminado':
        return 'Eliminado';
      default:
        return status;
    }
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>
            <FaScaleBalanced color="#0284c7" />
            Tabela de Temporalidade (TTD) & Gestão da CPAD
          </Title>
          <p style={{ color: '#64748b', marginTop: 4 }}>
            Controle do ciclo de vida documental, prazos de custódia e emissão de Termos de Avaliação e Eliminação.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={handleRecalculateAll} disabled={loading}>
            <FaRotate /> Recalcular TTD do Acervo
          </Button>
          <Button
            variant="danger"
            onClick={() => handleOpenTermModal('eliminacao')}
            disabled={selectedDocIds.length === 0}
          >
            <FaTrashCan /> Lavrar Termo de Eliminação ({selectedDocIds.length})
          </Button>
          <Button
            variant="primary"
            onClick={() => handleOpenTermModal('recolhimento_permanente')}
            disabled={selectedDocIds.length === 0}
          >
            <FaLandmark /> Termo de Guarda Permanente ({selectedDocIds.length})
          </Button>
        </div>
      </Header>

      {/* Cards de Métricas do Ciclo de Vida */}
      {summary && (
        <StatGrid>
          <StatCard color="#0284c7" active={activeStatus === 'corrente'} onClick={() => setActiveStatus('corrente')}>
            <div className="label">
              <FaHourglassHalf color="#0284c7" /> Fase Corrente (Setorial)
            </div>
            <div className="value">{summary.corrente}</div>
          </StatCard>

          <StatCard color="#d97706" active={activeStatus === 'intermediario'} onClick={() => setActiveStatus('intermediario')}>
            <div className="label">
              <FaBoxArchive color="#d97706" /> Fase Intermediária (Arquivo Geral)
            </div>
            <div className="value">{summary.intermediario}</div>
          </StatCard>

          <StatCard color="#ef4444" active={activeStatus === 'apto_eliminacao'} onClick={() => setActiveStatus('apto_eliminacao')}>
            <div className="label">
              <FaTriangleExclamation color="#ef4444" /> Apto para Eliminação
            </div>
            <div className="value">{summary.aptoEliminacao}</div>
          </StatCard>

          <StatCard color="#10b981" active={activeStatus === 'recolhido_permanente'} onClick={() => setActiveStatus('recolhido_permanente')}>
            <div className="label">
              <FaLandmark color="#10b981" /> Guarda Permanente
            </div>
            <div className="value">{summary.recolhidoPermanente}</div>
          </StatCard>

          <StatCard color="#64748b" active={activeStatus === 'eliminado'} onClick={() => setActiveStatus('eliminado')}>
            <div className="label">
              <FaTrashCan color="#64748b" /> Documentos Eliminados
            </div>
            <div className="value">{summary.eliminado}</div>
          </StatCard>
        </StatGrid>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <Button
          variant={activeTab === 'documents' ? 'primary' : 'secondary'}
          onClick={() => setActiveTab('documents')}
        >
          <FaFileCircleCheck /> Documentos no Acervo ({documents.length})
        </Button>
        <Button
          variant={activeTab === 'terms' ? 'primary' : 'secondary'}
          onClick={() => setActiveTab('terms')}
        >
          <FaFileSignature /> Termos Lavrados da CPAD ({terms.length})
        </Button>
      </div>

      {activeTab === 'documents' ? (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="Buscar documento..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
              />
              <Button type="submit" variant="secondary" style={{ padding: '8px 14px' }}>
                <FaFilter /> Filtrar
              </Button>
            </form>

            <div style={{ fontSize: 13, color: '#64748b' }}>
              Filtro ativo: <strong>{activeStatus.toUpperCase()}</strong> | Selecionados:{' '}
              <strong>{selectedDocIds.length}</strong>
            </div>
          </div>

          <Table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selectedDocIds.length > 0 && selectedDocIds.length === documents.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Documento</th>
                <th>Tipologia / Código</th>
                <th>Data Base</th>
                <th>Limite Corrente</th>
                <th>Limite Intermediário</th>
                <th>Status do Ciclo</th>
                <th>Destinação Final</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>
                    Nenhum documento encontrado neste estágio de temporalidade.
                  </td>
                </tr>
              ) : (
                documents.map((doc) => (
                  <tr key={doc._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDocIds.includes(doc._id)}
                        onChange={() => handleSelectDoc(doc._id)}
                      />
                    </td>
                    <td>
                      <strong>{doc.name}</strong>
                      {doc.eliminationLog?.termNumber && (
                        <div style={{ fontSize: 11, color: '#ef4444' }}>
                          Termo: {doc.eliminationLog.termNumber}
                        </div>
                      )}
                    </td>
                    <td>
                      {doc.documentTypeInfo ? (
                        <div>
                          <div>{doc.documentTypeInfo.name}</div>
                          <code style={{ fontSize: 11, color: '#64748b' }}>
                            {doc.documentTypeInfo.classificationCode || '000.0'}
                          </code>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>Não Classificado</span>
                      )}
                    </td>
                    <td>
                      {doc.documentDate ? format(new Date(doc.documentDate), 'dd/MM/yyyy') : '—'}
                    </td>
                    <td>
                      {doc.currentPhaseUntil ? format(new Date(doc.currentPhaseUntil), 'dd/MM/yyyy') : '—'}
                    </td>
                    <td>
                      {doc.intermediatePhaseUntil
                        ? format(new Date(doc.intermediatePhaseUntil), 'dd/MM/yyyy')
                        : '—'}
                    </td>
                    <td>
                      <Badge variant={doc.retentionStatus}>{renderStatusLabel(doc.retentionStatus)}</Badge>
                    </td>
                    <td>
                      {doc.documentTypeInfo?.finalDestination === 'eliminacao' ? (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>Eliminação</span>
                      ) : (
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>Guarda Permanente</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card>
      ) : (
        <Card>
          <h2 style={{ fontSize: 18, marginBottom: 16 }}>Histórico de Termos Oficiais da CPAD</h2>
          <Table>
            <thead>
              <tr>
                <th>Número do Termo</th>
                <th>Tipo de Termo</th>
                <th>Data da Reunião</th>
                <th>Qtd. Documentos</th>
                <th>Comissão / Responsável</th>
                <th>Fundamentação Legal</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {terms.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>
                    Nenhum Termo da CPAD emitido até o momento.
                  </td>
                </tr>
              ) : (
                terms.map((t) => (
                  <tr key={t._id}>
                    <td><strong>{t.termNumber}</strong></td>
                    <td>
                      <Badge variant={t.type === 'eliminacao' ? 'apto_eliminacao' : 'recolhido_permanente'}>
                        {t.type === 'eliminacao' ? 'Eliminação Documental' : 'Guarda Permanente'}
                      </Badge>
                    </td>
                    <td>{format(new Date(t.evaluationDate), 'dd/MM/yyyy')}</td>
                    <td><strong>{t.documentCount} doc(s)</strong></td>
                    <td>
                      <div>{t.responsibleUser?.name || 'Comissão'}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {t.committeeMembers?.join(', ')}
                      </div>
                    </td>
                    <td style={{ fontSize: 12 }}>{t.legalBasis || 'TTD Institucional'}</td>
                    <td>
                      <Button
                        variant="secondary"
                        style={{ padding: '6px 10px' }}
                        onClick={() => window.print()}
                      >
                        <FaPrint /> Imprimir
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Modal de Lavratura de Termo da CPAD */}
      {isTermModalOpen && (
        <ModalOverlay>
          <ModalBox>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#1e293b' }}>
              {termType === 'eliminacao'
                ? 'Lavrar Termo de Eliminação Documental (CPAD)'
                : 'Lavrar Termo de Recolhimento Permanente (CPAD)'}
            </h2>

            <form onSubmit={handleSaveTerm}>
              <FormGroup>
                <label>Número do Termo / Ano *</label>
                <input
                  type="text"
                  value={termNumber}
                  onChange={(e) => setTermNumber(e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label>Quantidade de Documentos Vinculados</label>
                <input type="text" disabled value={`${selectedDocIds.length} documento(s) selecionado(s)`} />
              </FormGroup>

              <FormGroup>
                <label>Membros da Comissão Permanente de Avaliação (CPAD)</label>
                <input
                  type="text"
                  value={committeeMembers}
                  onChange={(e) => setCommitteeMembers(e.target.value)}
                  placeholder="Nome do Presidente, Arquivista, etc."
                />
              </FormGroup>

              <FormGroup>
                <label>Fundamentação Legal / Base Normativa *</label>
                <input
                  type="text"
                  value={legalBasis}
                  onChange={(e) => setLegalBasis(e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label>Justificativa e Parecer da CPAD *</label>
                <textarea
                  rows={4}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  required
                />
              </FormGroup>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <Button type="button" variant="secondary" onClick={() => setIsTermModalOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant={termType === 'eliminacao' ? 'danger' : 'primary'}>
                  Confirmar e Lavrar Termo
                </Button>
              </div>
            </form>
          </ModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default CpadPage;
